import type { AnalysisResult } from "@/lib/music/analyzer-types";

/**
 * Jingle service.
 *
 * Fetches the Virtuosa audio identity from the Symfony API. The jingle is
 * sourced from `api/data/jingle.vef.yaml` (a VEF file editable in git, no
 * frontend release needed) and served by `GetJingleController` as a fresh
 * `AnalysisResult`, the same shape used everywhere else in the app, so
 * `createSongFromRawAnalysis()` can consume it with no special path.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

export async function getJingle(): Promise<AnalysisResult> {
  const response = await fetch(`${API_BASE_URL}/api/jingle`);

  if (!response.ok) {
    throw new Error(`Failed to fetch jingle: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Total playback duration of the jingle, in milliseconds. Used by the
 * caller to schedule the post-jingle cleanup (pause + reveal empty
 * overlay) without polling the transport.
 */
export function computeJingleDurationMs(result: AnalysisResult): number {
  if (result.notes.length === 0) {
    return 0;
  }

  const lastNote = result.notes[result.notes.length - 1];
  const endTick = lastNote.tick + lastNote.durationTicks;
  const ppq = result.tempo.ppq;
  const bpm = result.tempo.defaultBpm;
  const seconds = (endTick / ppq) * (60 / bpm);

  return Math.ceil(seconds * 1000);
}
